import type { TaxResidencySource } from '@1trading/fast-api-domain';
import { HTTPError } from 'ky';
import { bopApi } from '@/lib/api/client';

// Personal-identity family of bop-api endpoints (PII + Sumsub). Every GET on these
// endpoints is server-audited (pii-audit log rows) — callers must never prefetch;
// the PII/KYC tabs gate every request behind an operator justification.

export interface PersonalIdentityAddress {
  /** Server splits the stored string on ',' — lines must never contain commas. */
  addressLines?: string[];
  /** ISO country code */
  country?: string;
  locality?: string;
  postalCode?: string;
}

export interface PersonalIdentityBirthInfo {
  /** YYYY-MM-DD */
  date?: string;
  country?: string;
  locality?: string;
}

export interface PersonalIdentityPhoneNumber {
  /** e.g. '+44' */
  areaCode?: string;
  mobilePhone?: string;
}

export interface PersonalIdentityTaxResidence {
  /** ISO country code */
  country: string;
  /** Absent where the country issues none */
  taxId?: string;
}

export interface PersonalIdentityTaxResidency {
  residences: PersonalIdentityTaxResidence[];
  /** When the user affirmed it, ISO 8601 */
  affirmedAt?: string;
  source?: TaxResidencySource;
}

/** GET /v1/user/personal-identity/{pid} response. Email is NOT part of the record. */
export interface PersonalIdentity {
  pid: string;
  firstName?: string;
  lastName?: string;
  addressOfResidency?: PersonalIdentityAddress;
  birthInfo?: PersonalIdentityBirthInfo;
  /** ISO */
  citizenship?: string;
  /** ISO */
  citizenship2?: string;
  phoneNumber?: PersonalIdentityPhoneNumber;
  /** @deprecated Has no issuing country; read `taxResidency` */
  taxId?: string;
  taxResidency?: PersonalIdentityTaxResidency;
}

/**
 * PUT /v1/user/personal-identity/{pid} body.
 * ⚠ FULL-REPLACE semantics: any attribute absent from the body is DELETED from the
 * record server-side — always send the complete current state.
 */
export interface PersonalIdentityUpdate {
  firstName?: string;
  lastName?: string;
  addressOfResidency?: PersonalIdentityAddress;
  birthInfo?: PersonalIdentityBirthInfo;
  citizenship?: string;
  citizenship2?: string;
  phoneNumber?: PersonalIdentityPhoneNumber;
  /** @deprecated Has no issuing country; send `taxResidency` */
  taxId?: string;
  /**
   * The exception to full-replace: left out, the stored declaration is kept. Sent, it replaces it, dated when the user
   * affirmed it (ISO 8601, not in the future).
   */
  taxResidency?: { residences: PersonalIdentityTaxResidence[]; affirmedAt: string };
}

export interface PersonalIdentityUpdateResult {
  message?: string;
}

/** One S3 object version of the user's PII file, body inlined. */
export interface PersonalIdentityVersion {
  key: string;
  versionId: string;
  isLatest: boolean;
  /** ISO 8601 */
  lastModified: string;
  etag: string;
  size: number;
  storageClass?: string;
  contentType?: string;
  metadata: Record<string, string>;
  /** Full PII snapshot for that version — keys NOT guaranteed to match PersonalIdentity; render generically. */
  body: Record<string, unknown> | null;
}

export interface SumsubResource {
  id?: string;
  createdAt?: string;
  /** ⚠ both spellings occur in the wild */
  mimetype?: string;
  mimeType?: string;
  [key: string]: unknown;
}

export interface SumsubInlineImage {
  id: string;
  /** e.g. 'image/jpeg' */
  contentType?: string;
  createdAt?: string;
  /** full image bytes, base64 — payloads can be MBs */
  dataBase64: string;
}

/** GET /v1/user/personal-identity/sumsub/full/{pid} response. */
export interface SumsubFullPayload {
  applicant: Record<string, unknown>;
  resources: SumsubResource[];
  images: SumsubInlineImage[];
}

type MaybeEnveloped<T> = T | { statusCode: number; body: T | string };

/**
 * The personal-identity lambda family sometimes returns an API Gateway
 * envelope `{statusCode, body}` inside an HTTP 200. Unwrap it here in the service layer —
 * deliberately NOT generalised into the shared ky client.
 */
const unwrapLambdaEnvelope = <T>(payload: MaybeEnveloped<T>, fallbackMessage: string): T => {
  if (payload && typeof payload === 'object' && 'statusCode' in payload && 'body' in payload) {
    const { statusCode, body } = payload as { statusCode: number; body: T | string };
    if (typeof statusCode === 'number' && statusCode !== 200) {
      // Non-200 smuggled inside a 200 — surface as an error
      throw new Error(typeof body === 'string' ? body : fallbackMessage);
    }
    return body as T;
  }
  return payload as T;
};

/**
 * Legacy error-message derivation chain, first match wins:
 * 1. non-empty string body → verbatim; 2. object `body` string (nested envelope error);
 * 3. object `message` string; 4. object `error` string (ApiError also promotes this);
 * 5. per-endpoint fallback.
 */
const deriveErrorMessage = async (error: HTTPError, fallbackMessage: string): Promise<string> => {
  try {
    const text = await error.response.clone().text();
    if (!text) return fallbackMessage;
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return text;
    }
    if (typeof parsed === 'string' && parsed !== '') return parsed;
    if (parsed && typeof parsed === 'object') {
      const body = parsed as Record<string, unknown>;
      if (typeof body.body === 'string' && body.body !== '') return body.body;
      if (typeof body.message === 'string' && body.message !== '') return body.message;
      if (typeof body.error === 'string' && body.error !== '') return body.error;
    }
    return fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

/**
 * Envelope unwrap + message derivation around one request. Upstream 4xx bodies (e.g. the
 * legitimate 404 "No personal identity record found…") become plain Errors whose message
 * renders as a normal alert — never a crash.
 */
const requestEnveloped = async <T>(request: Promise<MaybeEnveloped<T>>, fallbackMessage: string): Promise<T> => {
  try {
    return unwrapLambdaEnvelope(await request, fallbackMessage);
  } catch (error) {
    if (error instanceof HTTPError) throw new Error(await deriveErrorMessage(error, fallbackMessage));
    throw error;
  }
};

/** Server-audited on EVERY call (pii-audit row written before the read) — never prefetch. */
export const getPersonalIdentity = (pid: string): Promise<PersonalIdentity> =>
  requestEnveloped(bopApi.get(`v1/user/personal-identity/${pid}`).json<MaybeEnveloped<PersonalIdentity>>(), 'Failed to fetch personal identity');

export class TaxResidencyChangedError extends Error {
  constructor() {
    super('The user changed their tax residency while you were editing. Their latest record has been loaded; make your change again.');
    this.name = 'TaxResidencyChangedError';
  }
}

/** ⚠ Full-replace PUT — the body must carry the complete current state. */
export const updatePersonalIdentity = async (pid: string, body: PersonalIdentityUpdate): Promise<PersonalIdentityUpdateResult> => {
  try {
    return await requestEnveloped(
      bopApi.put(`v1/user/personal-identity/${pid}`, { json: body }).json<MaybeEnveloped<PersonalIdentityUpdateResult>>(),
      'Failed to update personal identity',
    );
  } catch (error) {
    if (error instanceof Error && error.message === 'TAX_RESIDENCY_CHANGED') throw new TaxResidencyChangedError();

    throw error;
  }
};

/**
 * Full historical PII snapshots — sits behind the same justification gate as the latest
 * record. No envelope quirk on this endpoint (the unwrap only applies to the
 * personal-identity GET/PUT and Sumsub calls), but the message-derivation chain still applies.
 */
export const getPersonalIdentityVersions = async (pid: string): Promise<PersonalIdentityVersion[]> => {
  try {
    return await bopApi.get(`v1/user/personal-identity/versions/${pid}`).json<PersonalIdentityVersion[]>();
  } catch (error) {
    if (error instanceof HTTPError) throw new Error(await deriveErrorMessage(error, 'Failed to fetch personal identity versions'));
    throw error;
  }
};

/** Sumsub applicant + inline images — same envelope quirk as the personal-identity GET. */
export const getSumsubFull = (pid: string): Promise<SumsubFullPayload> =>
  requestEnveloped(bopApi.get(`v1/user/personal-identity/sumsub/full/${pid}`).json<MaybeEnveloped<SumsubFullPayload>>(), 'Failed to retrieve Sumsub full data');
